import React from "react";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import {
  T2W_EDITABLE_FIELDS,
  T2W_FOOTER_DISPLAY_LABEL,
  isTrendsFooterOn,
  footerToggleValue,
  isTrendsFieldOverLimit,
  isTrendsFormOverLimit,
} from "./tpTrends2WatchUtils";

function padCount(count, max) {
  const width = String(max ?? 0).length;
  return `${String(count).padStart(width, "0")}/${max}`;
}

function getFieldMeta(formData, field) {
  const value = formData?.[field?.key] ?? "";
  const maxLength = field?.maxLength;
  const charCount = String(value).length;
  return {
    value,
    maxLength,
    charCount,
    overLimit: isTrendsFieldOverLimit(value, maxLength),
    atLimit: maxLength != null && charCount >= maxLength,
  };
}

const SentenceTrendsEditorModal = ({
  open,
  title = "Edit Trends to Watch",
  headerVariant = "default",
  modalMode = null,
  contextTitle = "",
  neighborhood = "",
  fields = T2W_EDITABLE_FIELDS,
  formData,
  isDirty,
  onFieldChange,
  onClose,
  onSave,
  onResetToDefault,
  canResetToDefault = false,
  saving,
  saveSuccess,
  saveError,
}) => {
  const isLocked = Boolean(saving || saveSuccess);
  const headingField = (fields || []).find((field) => field.kind === "input");
  const textField = (fields || []).find((field) => field.kind === "textarea");
  const footerField = (fields || []).find(
    (field) => field.kind === "footerToggle",
  );
  const originalHeading = String(formData?.[headingField?.orgKey] ?? "").trim();
  const originalText = String(formData?.[textField?.orgKey] ?? "").trim();
  const headingMeta = getFieldMeta(formData, headingField);
  const textMeta = getFieldMeta(formData, textField);
  const hasOverLimit = isTrendsFormOverLimit(formData, fields);
  const saveLabel = saveSuccess
    ? "SAVED"
    : saving
      ? "UPDATING..."
      : "UPDATE YOUR CHANGES";
  const isSection = headerVariant === "section";
  const isTakeaway = modalMode === "takeaway";
  const headingLabel = isTakeaway ? "Take Away Title" : "Section Title";
  const textLabel = isTakeaway ? "Take Away Text" : "Section Text";

  const renderDefaultField = (field) => {
    if (field.kind === "footerToggle") {
      const checked = isTrendsFooterOn(formData?.[field.key]);
      return (
        <label
          key={field.key}
          className="sentence-trends-editor-footer-toggle"
          htmlFor={field.key}
        >
          <input
            id={field.key}
            className="sentence-trends-editor-footer-checkbox"
            type="checkbox"
            checked={checked}
            onChange={(e) =>
              onFieldChange(field.key, footerToggleValue(e.target.checked))
            }
            disabled={isLocked}
          />
          <span className="sentence-trends-editor-footer-toggle-text">
            {field.label || T2W_FOOTER_DISPLAY_LABEL}
          </span>
        </label>
      );
    }
    const meta = getFieldMeta(formData, field);
    const FieldTag = field.kind === "textarea" ? "textarea" : "input";
    return (
      <div key={field.key} className="sentence-trends-editor-field">
        <label className="sentence-trends-editor-label" htmlFor={field.key}>
          {field.label}
        </label>
        <FieldTag
          id={field.key}
          className={`${
            field.kind === "textarea"
              ? "sentence-trends-editor-textarea"
              : "sentence-trends-editor-input"
          }${meta.overLimit ? " is-over-limit" : ""}`}
          value={meta.value}
          maxLength={meta.maxLength}
          onChange={(e) => onFieldChange(field.key, e.target.value)}
          disabled={isLocked}
          rows={field.kind === "textarea" ? 4 : undefined}
          type={field.kind === "textarea" ? undefined : "text"}
        />
        {meta.maxLength != null ? (
          <span
            className={`sentence-trends-editor-counter${
              meta.overLimit || meta.atLimit ? " is-error" : ""
            }`}
          >
            {meta.charCount}/{meta.maxLength}
          </span>
        ) : null}
        {meta.overLimit ? (
          <p className="sentence-trends-editor-limit-error" role="alert">
            {field.label} cannot exceed {meta.maxLength} characters. Shorten
            your text before saving.
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <MailProDialog
      open={open}
      onClose={isLocked ? undefined : onClose}
      disableEscapeKeyDown={isLocked}
      classes={{
        paper: `sentence-trends-editor-dialog${isSection ? " is-section" : ""}`,
      }}
      PaperProps={{
        style: {
          width: "90vw",
          minWidth: isSection ? "min(860px, 90vw)" : "min(860px, 90vw)",
          maxWidth: isSection ? 860 : 860,
        },
      }}
    >
      <MailProDialogHeader
        className={`sentence-trends-editor-header${
          isSection ? " sentence-trends-editor-header--section" : ""
        }`}
      >
        {isSection ? (
          <>
            <div className="sentence-trends-editor-title">{title}</div>
            <div className="sentence-trends-editor-header-context">
              {contextTitle ? (
                <div className="sentence-trends-editor-header-context-title">
                  {contextTitle}
                </div>
              ) : null}
              {neighborhood ? (
                <div className="sentence-trends-editor-header-neighborhood">
                  {neighborhood}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="sentence-trends-editor-title">{title}</div>
        )}
      </MailProDialogHeader>
      <MailProDialogBody
        className={`sentence-trends-editor-body${
          isSection ? " is-section" : ""
        }`}
      >
        {isSection ? (
          <>
            <div
              className="sentence-trends-editor-original"
              aria-readonly="true"
            >
              <div className="sentence-trends-editor-original-top">
                <div className="sentence-trends-editor-original-label">
                  Default Title & Text
                </div>
                <div className="sentence-trends-editor-original-heading">
                  {originalHeading}
                </div>
              </div>
              <div className="sentence-trends-editor-original-body">
                <div className="sentence-trends-editor-original-text">
                  {originalText}
                </div>
              </div>
            </div>

            <div
              className={`sentence-trends-editor-section-card${
                modalMode === "trend2"
                  ? " is-card-2"
                  : modalMode === "trend3" || isTakeaway
                    ? " is-card-3"
                    : " is-card-1"
              }`}
            >
              <div className="sentence-trends-editor-section-row is-title">
                <div className="sentence-trends-editor-section-meta">
                  <div className="sentence-trends-editor-section-meta-label">
                    {headingLabel}
                  </div>

                  <div
                    className={`sentence-trends-editor-section-meta-count${
                      headingMeta.overLimit || headingMeta.atLimit
                        ? " is-error"
                        : ""
                    }`}
                  >
                    Character Count:{" "}
                    {padCount(headingMeta.charCount, headingMeta.maxLength)}
                  </div>
                </div>
                <input
                  id={headingField?.key}
                  className={`sentence-trends-editor-section-title-input${
                    headingMeta.overLimit ? " is-over-limit" : ""
                  }`}
                  value={headingMeta.value}
                  maxLength={headingMeta.maxLength}
                  onChange={(e) =>
                    onFieldChange(headingField.key, e.target.value)
                  }
                  disabled={isLocked}
                  type="text"
                />
              </div>

              <div className="sentence-trends-editor-section-row is-text">
                <div className="sentence-trends-editor-section-meta">
                  <div className="sentence-trends-editor-section-meta-label">
                    {textLabel}
                  </div>

                  <div
                    className={`sentence-trends-editor-section-meta-count${
                      textMeta.overLimit || textMeta.atLimit ? " is-error" : ""
                    }`}
                  >
                    Character Count:{" "}
                    {padCount(textMeta.charCount, textMeta.maxLength)}
                  </div>
                </div>
                <textarea
                  id={textField?.key}
                  className={`sentence-trends-editor-textarea${
                    textMeta.overLimit ? " is-over-limit" : ""
                  }`}
                  value={textMeta.value}
                  maxLength={textMeta.maxLength}
                  onChange={(e) => onFieldChange(textField.key, e.target.value)}
                  disabled={isLocked}
                  rows={4}
                />
              </div>

              {headingMeta.overLimit || textMeta.overLimit ? (
                <p className="sentence-trends-editor-limit-error" role="alert">
                  {headingMeta.overLimit
                    ? `${headingLabel} cannot exceed ${headingMeta.maxLength} characters. `
                    : ""}
                  {textMeta.overLimit
                    ? `${textLabel} cannot exceed ${textMeta.maxLength} characters. `
                    : ""}
                  Shorten your text before saving.
                </p>
              ) : null}

              {footerField ? (
                <label
                  className="sentence-trends-editor-footer-toggle is-in-card"
                  htmlFor={footerField.key}
                >
                  <input
                    id={footerField.key}
                    className="sentence-trends-editor-footer-checkbox"
                    type="checkbox"
                    checked={isTrendsFooterOn(formData?.[footerField.key])}
                    onChange={(e) =>
                      onFieldChange(
                        footerField.key,
                        footerToggleValue(e.target.checked),
                      )
                    }
                    disabled={isLocked}
                  />
                  <span className="sentence-trends-editor-footer-toggle-text">
                    {footerField.label || T2W_FOOTER_DISPLAY_LABEL}
                  </span>
                </label>
              ) : null}
            </div>
          </>
        ) : (
          (fields || []).map(renderDefaultField)
        )}
      </MailProDialogBody>
      {saveError ? (
        <p className="sentence-trends-editor-save-error" role="alert">
          {saveError}
        </p>
      ) : null}
      <div className="sentence-trends-editor-footer">
        <div className="sentence-trends-editor-footer-left">
          <MailProButton
            variant="outlined"
            label="CANCEL"
            additionalClass="sentence-trends-editor-btn-cancel"
            disabled={isLocked}
            onClick={onClose}
          />
        </div>
        <div className="sentence-trends-editor-footer-center">
          <MailProButton
            variant="filled"
            label={saveLabel}
            additionalClass={`sentence-trends-editor-btn-update${
              saveSuccess ? " is-saved" : ""
            }`}
            disabled={saving || hasOverLimit || (!isDirty && !saveSuccess)}
            onClick={onSave}
          />
        </div>
        <div className="sentence-trends-editor-footer-right">
          <MailProButton
            variant="filled"
            label="Reset to Default Text"
            additionalClass="sentence-trends-editor-btn-reset"
            disabled={isLocked || !canResetToDefault}
            onClick={onResetToDefault}
          />
        </div>
      </div>
    </MailProDialog>
  );
};

export default SentenceTrendsEditorModal;
